import { useCallback, useEffect, useRef, useState } from 'react';
import { RequestFailed } from './api';

export type AsyncState<T> = { data: T | null; loading: boolean; error: string | null };

/** Загрузка данных с отменой устаревшего ответа: иначе быстрый переход между вкладками показывает чужие данные. */
export function useAsync<T>(fn: () => Promise<T>, deps: unknown[] = []): AsyncState<T> & { reload: () => void } {
  const [state, setState] = useState<AsyncState<T>>({ data: null, loading: true, error: null });
  const generation = useRef(0);

  const run = useCallback(() => {
    const mine = ++generation.current;
    setState((s) => ({ ...s, loading: true, error: null }));
    fn()
      .then((data) => {
        if (mine === generation.current) setState({ data, loading: false, error: null });
      })
      .catch((e: unknown) => {
        if (mine !== generation.current) return;
        const message = e instanceof RequestFailed ? e.error.message : 'Не удалось загрузить данные';
        setState({ data: null, loading: false, error: message });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    run();
    return () => {
      generation.current++;
    };
  }, [run]);

  return { ...state, reload: run };
}

/**
 * Ключ идемпотентности покупки. Живёт, пока пользователь не сменил товар:
 * повторный тап по кнопке приходит с тем же ключом и не списывает бонусы дважды.
 */
export function useIdempotencyKey(seed: string): string {
  const [key, setKey] = useState(() => `${seed}:${crypto.randomUUID()}`);
  useEffect(() => setKey(`${seed}:${crypto.randomUUID()}`), [seed]);
  return key;
}
